import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { CURRENCIES, getCurrencyMeta, searchCurrencies, getRecentCurrencies, rememberCurrency } from '@/lib/currencies';

const MAX_RESULTS = 80;

// Currencies an administrator registered that ISO 4217 does not carry (ZiG
// began life as one). Fetched once per page load and shared by every picker,
// so what finance registers is what every form offers.
let customCache = null;
const useCustomCurrencies = () => {
  const [custom, setCustom] = useState(customCache || []);
  useEffect(() => {
    if (customCache) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/catalogue`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        customCache = (d?.items || []).filter((i) => i.custom)
          .map((i) => ({ code: i.code, name: i.name || i.code, symbol: i.symbol || i.code, countries: '' }));
        setCustom(customCache);
      })
      .catch(() => { customCache = []; });
  }, []);
  return custom;
};

/**
 * Searchable ISO 4217 currency picker. Type a code, currency name or country
 * ("rand", "Kenya", "ZWL") to filter instantly. `homeCurrency` is surfaced as
 * the assembly/organisation default alongside recently used codes.
 */
export const CurrencySelect = ({
  value,
  onChange,
  homeCurrency,
  disabled = false,
  className,
  placeholder = 'Select currency',
  testId = 'currency-select',
  showHomeHint = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const meta = value ? getCurrencyMeta(value) : null;
  const home = homeCurrency ? homeCurrency.toUpperCase() : '';

  const custom = useCustomCurrencies();

  const { favourites, results } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const customMatches = q
      ? custom.filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(q))
      : custom;
    const list = [...customMatches, ...(query ? searchCurrencies(query) : CURRENCIES)];
    if (query) return { favourites: [], results: list.slice(0, MAX_RESULTS) };
    const favCodes = [...new Set([home, value, ...getRecentCurrencies()].filter(Boolean))];
    return {
      favourites: favCodes.map(getCurrencyMeta),
      results: list.slice(0, MAX_RESULTS),
    };
  }, [query, home, value, custom, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (code) => {
    rememberCurrency(code);
    onChange?.(code);
    setOpen(false);
    setQuery('');
  };

  const renderItem = (c, keyPrefix = '') => (
    <CommandItem
      key={`${keyPrefix}${c.code}`}
      value={`${keyPrefix}${c.code}`}
      onSelect={() => pick(c.code)}
      className="gap-2"
      data-testid={`currency-option-${c.code}`}
    >
      <span className="w-10 shrink-0 text-xs font-semibold text-slate-500">{c.symbol}</span>
      <span className="font-medium">{c.code}</span>
      <span className="truncate text-xs text-slate-500">{c.name}</span>
      {c.code === value && <Check className="ml-auto h-4 w-4 text-emerald-600" />}
    </CommandItem>
  );

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid={testId}
          >
            <span className="flex items-center gap-1.5 truncate">
              {meta ? (
                <>
                  <span className="text-slate-500">{meta.symbol}</span>
                  <span className="font-medium">{meta.code}</span>
                </>
              ) : (
                <span className="text-slate-400">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full max-w-[320px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search code, currency or country…"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-search`}
            />
            <CommandList className="max-h-72">
              <CommandEmpty>No currency matches “{query}”.</CommandEmpty>
              {favourites.length > 0 && (
                <CommandGroup heading="Assembly default & recent">
                  {favourites.map(c => renderItem(c, 'fav-'))}
                </CommandGroup>
              )}
              <CommandGroup heading={query ? 'Matches' : 'All currencies (A–Z)'}>
                {results.map(c => renderItem(c))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showHomeHint && home && (
        <p className={cn('mt-1 flex items-center gap-1 text-[10px]', value === home ? 'text-slate-400' : 'text-amber-600')}>
          <Globe className="h-3 w-3" />
          {value === home ? `Assembly default (${home})` : `Overriding assembly default (${home})`}
        </p>
      )}
    </div>
  );
};

export default CurrencySelect;
